/**
 * Друкована форма «Акт наданих послуг з переробки» (довільна форма,
 * ч.692): ми = ВИКОНАВЕЦЬ — дзеркало акта приймання продукції з
 * переробки на боці замовника. Замовник + договір; секції: Продукція
 * (передаємо замовнику), Послуги з ПДВ (наш рахунок — друкується
 * завжди), Використана сировина замовника (к-сть + декл. вартість);
 * підписи. Канон друку ч.669/677.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { processingServicesSales } from '@/api/processingServicesSales';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import { contractLabel } from '@/shared/utils/labels';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string | null | undefined) =>
  Number(n ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ProcessingServicesSalesPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['processing-services-sales', id],
    queryFn: () => processingServicesSales.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const contractsQ = useQuery({
    queryKey: ['contracts', d?.organization_id, d?.customer_contractor_id],
    queryFn: () => refs.contracts({
      organization_id: d!.organization_id,
      contractor_id: d!.customer_contractor_id,
    }),
    enabled: !!d?.organization_id && !!d?.customer_contractor_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження акта…</div>;
  }

  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const customer = cQ.data?.items.find((c) => c.id === d.customer_contractor_id);
  const contract = (contractsQ.data ?? []).find((c) => c.id === d.contract_id);
  const uomOf = (it: { nomenclature_id: string; uom_id?: string | null }) => {
    if (it.uom_id) return uomById.get(it.uom_id) ?? 'шт';
    const n = nomById.get(it.nomenclature_id);
    return (n?.base_uom_id && uomById.get(n.base_uom_id)) || 'шт';
  };
  const nomCell = (nid: string) => {
    const n = nomById.get(nid);
    return n ? <>{n.name}{n.code && <span style={{ color: '#555' }}> ({n.code})</span>}</> : <>—</>;
  };
  const services = d.services ?? [];
  const svcShown = services.length > 0;
  const svcTotal = svcShown
    ? services.reduce((s, x) => s + Number(x.amount ?? 0), 0)
    : Number(d.service_amount);
  const svcVat = svcShown
    ? services.reduce((s, x) => s + Number(x.vat_amount ?? 0), 0)
    : Number(d.vat_amount);
  const declTotal = d.materials.reduce((s, m) => s + Number(m.declared_value ?? 0), 0);
  const returned = d.returned_materials ?? [];
  const retDeclTotal = returned.reduce((s, m) => s + Number(m.declared_value ?? 0), 0);

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .pss-table { width: 100%; border-collapse: collapse; margin: 0.35rem 0 0.75rem; font-size: 9pt; line-height: 1.15; }
        .pss-table th, .pss-table td { border: 1px solid #000; padding: 1px 4px; }
        .pss-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
        .pss-sec { font-weight: 700; margin: 0.6rem 0 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.processing_services_sales',
        documentId: d.id,
        filename: `Послуги переробки ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Акт наданих послуг з переробки № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10pt', marginBottom: '0.25rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '55%' }}>
              <span style={{ color: '#555' }}>Замовник: </span>
              <strong>{customer?.name ?? '—'}</strong>
              {partyCodesText(customer) && <span style={{ color: '#555' }}> ({partyCodesText(customer)})</span>}
            </td>
            <td>
              <span style={{ color: '#555' }}>Договір: </span>
              {contract ? contractLabel(contract) : 'без договору'}
            </td>
          </tr>
        </tbody>
      </table>

      {d.products.length > 0 && (<>
        <div className="pss-sec">Продукція з давальницької сировини (передається замовнику)</div>
        <table className="pss-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Продукція</th>
              <th style={{ width: 45 }}>Од.</th>
              <th style={{ width: 80 }}>К-сть</th>
            </tr>
          </thead>
          <tbody>
            {d.products.map((p, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nomCell(p.nomenclature_id)}</td>
                <td style={{ textAlign: 'center' }}>{uomOf(p)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(p.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>)}

      <div className="pss-sec">Надані послуги</div>
      <table className="pss-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Найменування послуги</th>
            <th style={{ width: 70 }}>К-сть</th>
            <th style={{ width: 80 }}>Ціна</th>
            <th style={{ width: 90 }}>Сума</th>
            <th style={{ width: 85 }}>ПДВ</th>
          </tr>
        </thead>
        <tbody>
          {svcShown ? services.map((sv, i) => (
            <tr key={i}>
              <td style={{ textAlign: 'center' }}>{i + 1}</td>
              <td>
                {sv.description
                  || (sv.nomenclature_id ? nomById.get(sv.nomenclature_id)?.name : '') || '—'}
              </td>
              <td style={{ textAlign: 'right' }}>{fmtQty(sv.quantity ?? '1')}</td>
              <td style={{ textAlign: 'right' }}>{sv.price != null ? fmtMoney(sv.price) : '—'}</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(sv.amount)}</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(sv.vat_amount)}</td>
            </tr>
          )) : (
            <tr>
              <td style={{ textAlign: 'center' }}>1</td>
              <td>Послуги з переробки давальницької сировини</td>
              <td style={{ textAlign: 'right' }}>1</td>
              <td style={{ textAlign: 'right' }}>—</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(d.service_amount)}</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(d.vat_amount)}</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={4} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmtMoney(svcTotal)}</td>
            <td style={{ textAlign: 'right' }}>{fmtMoney(svcVat)}</td>
          </tr>
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={5} style={{ textAlign: 'right' }}>Всього з ПДВ:</td>
            <td style={{ textAlign: 'right' }}>{fmtMoney(svcTotal + svcVat)}</td>
          </tr>
        </tfoot>
      </table>

      {d.materials.length > 0 && (<>
        <div className="pss-sec">Використана давальницька сировина замовника</div>
        <table className="pss-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Сировина</th>
              <th style={{ width: 45 }}>Од.</th>
              <th style={{ width: 80 }}>К-сть</th>
              <th style={{ width: 110 }}>Задекл. вартість</th>
            </tr>
          </thead>
          <tbody>
            {d.materials.map((m, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nomCell(m.nomenclature_id)}</td>
                <td style={{ textAlign: 'center' }}>{uomOf(m)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(m.quantity)}</td>
                <td style={{ textAlign: 'right' }}>
                  {m.declared_value != null ? fmtMoney(m.declared_value) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4} style={{ textAlign: 'right' }}>Разом:</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(declTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </>)}

      {returned.length > 0 && (<>
        <div className="pss-sec">Повернена невикористана давальницька сировина</div>
        <table className="pss-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Сировина</th>
              <th style={{ width: 45 }}>Од.</th>
              <th style={{ width: 80 }}>К-сть</th>
              <th style={{ width: 110 }}>Задекл. вартість</th>
            </tr>
          </thead>
          <tbody>
            {returned.map((m, i) => (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nomCell(m.nomenclature_id)}</td>
                <td style={{ textAlign: 'center' }}>{uomOf(m)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(m.quantity)}</td>
                <td style={{ textAlign: 'right' }}>
                  {m.declared_value != null ? fmtMoney(m.declared_value) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4} style={{ textAlign: 'right' }}>Разом:</td>
              <td style={{ textAlign: 'right' }}>{fmtMoney(retDeclTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </>)}

      <p style={{ margin: '0.5rem 0' }}>
        Послуги надано з давальницької сировини замовника; сировина і
        продукція залишаються власністю замовника. Сторони претензій щодо
        обсягу та якості не мають.
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Виконавець</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Замовник</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б., довіреність</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
